const MODULE_NAME = "_30_html_calendar_month";

import React, { useEffect, useState, useMemo, useRef } from "react";
import { cachedGet } from "./apiCache.js";
import { handleUIEvent } from "./eventLogger.js";
import CalendarSelector from "./_42_calendar_selector.js";
import "./styles.css";

const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function getMonthName(month) {
    return new Date(2000, month - 1, 1).toLocaleString(undefined, { month: "long" });
}

function getMonthlyTotal(grid) {
    if (!grid) return 0;
    let total = 0;
    for (const week of grid) {
        for (const cell of week) {
            if (cell && typeof cell.profit === "number") {
                total += cell.profit;
            }
        }
    }
    return total;
}

function getMonthlyTotalSymbol(grid) {
    if (!grid) return 0;
    let total = 0;
    for (const week of grid) {
        for (const cell of week) {
            if (cell && typeof cell.symbol_profit === "number") {
                total += cell.symbol_profit;
            }
        }
    }
    return total;
}

function getYearMonthFromDate(dateStr) {
    if (!dateStr) return { year: null, month: null };
    const d = new Date(dateStr);
    if (isNaN(d)) return { year: null, month: null };
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function formatMMDDYY(dateStr) {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const [yyyy, mm, dd] = parts;
    return `${mm}/${dd}/${yyyy.slice(2)}`;
}

export default function HtmlCalendarMonth(props) {
    const tagFilter = props.tagFilter || "";
    const refreshKey = props.refreshKey;
    const navigationDate = props.navigationDate || sessionStorage.getItem("navigationDate");

    let latestDateValue = props.latest_date;
    if (!latestDateValue) {
        latestDateValue = sessionStorage.getItem("latest_date");
    }

    const initialYearMonth = getYearMonthFromDate(navigationDate || latestDateValue);
    const [dateState, setDateState] = useState({
        year: initialYearMonth.year,
        month: initialYearMonth.month
    });
    const [latestYear, setLatestYear] = useState(getYearMonthFromDate(latestDateValue).year);
    const [latestMonth, setLatestMonth] = useState(getYearMonthFromDate(latestDateValue).month);
    const [calendarData, setCalendarData] = useState({});
    const [tooltip, setTooltip] = useState("Monthly calendar of daily P/L. Click a day to analyze.");
    const [plMethod, setPlMethod] = useState(() => sessionStorage.getItem("plMethod") || "fifo");

    const [mode, setMode] = useState("days");

    const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
    const [useSymbolProfit, setUseSymbolProfit] = useState(false);
    const [showOpenOrdersLine, setShowOpenOrdersLine] = useState(false);
    const [showOptionsLine, setShowOptionsLine] = useState(false);

    const additionalOptionsRef = useRef(null);

    const [analysisActiveDate, setAnalysisActiveDate] = useState(null);
    const [showAnalysisMode, setShowAnalysisMode] = useState(false);

    useEffect(() => {
        if (!showAdditionalOptions) return;

        function handleClickOutside(event) {
            if (
                additionalOptionsRef.current &&
                !additionalOptionsRef.current.contains(event.target)
            ) {
                setShowAdditionalOptions(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [showAdditionalOptions]);

    useEffect(() => {
        let dateToUse = navigationDate || props.latest_date || sessionStorage.getItem("latest_date");
        const { year, month } = getYearMonthFromDate(dateToUse);
        if (year && month) {
            setDateState(prev => {
                if (prev.year !== year || prev.month !== month) {
                    return { year, month };
                }
                return prev;
            });
            setLatestYear(year);
            setLatestMonth(month);
        }
    }, [navigationDate, props.latest_date]);

    useEffect(() => {
        if (mode === "months") {
            cachedGet(
                `${process.env.REACT_APP_API_URL}/api/html-calendar-month`,
                { mode: "months", tag_filter: tagFilter },
                { withCredentials: true }
            ).then(res => {
                setCalendarData(res.data);
                if (res.data.tooltip) setTooltip(res.data.tooltip);
            });
        } else if (dateState.year && dateState.month) {
            cachedGet(
                `${process.env.REACT_APP_API_URL}/api/html-calendar-month`,
                {
                    year: dateState.year,
                    month: dateState.month,
                    pl_method: plMethod,
                    refreshKey,
                    daily_profit_type: useSymbolProfit ? "daily_symbol" : "daily",
                    tag_filter: tagFilter
                },
                { withCredentials: true }
            ).then(res => {
                setCalendarData(res.data);
                if (res.data.tooltip) setTooltip(res.data.tooltip);
            });
        }
    }, [dateState.year, dateState.month, plMethod, useSymbolProfit, refreshKey, mode, tagFilter]);

    const openOrderRanges = useMemo(() => {
        if (!calendarData.open_orders) return [];
        return calendarData.open_orders.map(order => {
            const dates = [];
            const [yyyy, mm, dd] = order.open_date.split("-");
            const start = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
            for (let i = 0; i <= order.days_open; i++) {
                const d = new Date(start);
                d.setDate(start.getDate() + i);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                dates.push(`${y}-${m}-${day}`);
            }
            return dates;
        });
    }, [calendarData.open_orders]);

    const optionsOrderRanges = useMemo(() => {
        if (!calendarData.options_orders) return [];
        return calendarData.options_orders.map(order => {
            if (!order.open_date || !order.days_open || isNaN(order.days_open)) return [];
            const dates = [];
            const [yyyy, mm, dd] = order.open_date.split("-");
            const start = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
            for (let i = 0; i <= order.days_open; i++) {
                const d = new Date(start);
                d.setDate(start.getDate() + i);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                dates.push(`${y}-${m}-${day}`);
            }
            return dates;
        });
    }, [calendarData.options_orders]);

    const openOrderDateSet = useMemo(() => {
        if (!showOpenOrdersLine || openOrderRanges.length === 0) return new Set();
        return new Set(openOrderRanges.flat());
    }, [openOrderRanges, showOpenOrdersLine]);

    const optionsOrderDateSet = useMemo(() => {
        if (!showOptionsLine || optionsOrderRanges.length === 0) return new Set();
        return new Set(optionsOrderRanges.flat());
    }, [optionsOrderRanges, showOptionsLine]);

    function getOpenOrderLineIndices(week, dateSet) {
        let startIdx = null, endIdx = null;
        week.forEach((cell, idx) => {
            const cellDateStr = cell && cell.day
                ? getDateString(dateState.year, dateState.month, cell.day)
                : null;
            if (cellDateStr && dateSet.has(cellDateStr)) {
                if (startIdx === null) startIdx = idx;
                endIdx = idx;
            }
        });
        return startIdx !== null && endIdx !== null ? { startIdx, endIdx } : null;
    }

    useEffect(() => {
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            const handler = () => setPlMethod(sessionStorage.getItem("plMethod") || "fifo");
            window.addEventListener("plMethodChanged", handler);
            return () => window.removeEventListener("plMethodChanged", handler);
        }
    }, []);

    const [selectedDates, setSelectedDates] = useState({ date_1: null, date_2: null });
    const [dateClickCount, setDateClickCount] = useState(0);

    function getDateString(year, month, day) {
        if (!year || !month || !day) return "";
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }

    const screen = props.screen || window.location.pathname;
    const MODULE_LOCAL = "_30_html_calendar_month.js";

    const handleDayTileClick = (cell, e) => {
        if (!cell || !cell.day) return;
        const clickedDate = getDateString(dateState.year, dateState.month, cell.day);

        if (analysisActiveDate === clickedDate && showAnalysisMode) {
            setShowAnalysisMode(false);
            setAnalysisActiveDate(null);
            return;
        }

        setAnalysisActiveDate(clickedDate);
        setShowAnalysisMode(true);
        handleUIEvent(e, { action: "enter_analysis_mode", date: clickedDate }, null, screen, MODULE_LOCAL);
    };

    const exitAnalysisMode = () => {
        setShowAnalysisMode(false);
        setAnalysisActiveDate(null);
    };

    const handleCalendarSelectorChange = (dateStr) => {
        const { year, month } = getYearMonthFromDate(dateStr);
        if (year && month) {
            setDateState({ year, month });
        }
        handleUIEvent(null, { action: "calendar_selector_date_change", date: dateStr }, null, screen, MODULE_LOCAL);
    };

    const toggleAdditionalOptions = (e) => {
        setShowAdditionalOptions(prev => !prev);
        handleUIEvent(e, { action: "toggle_additional_options", expanded: !showAdditionalOptions }, null, screen, MODULE_LOCAL);
    };

    const handleSymbolProfitToggle = (e) => {
        const checked = e.target.checked;
        setUseSymbolProfit(checked);
        handleUIEvent(
            e,
            { action: "toggle_profit_mode", mode: checked ? "symbol_profit" : "daily_profit" },
            null,
            screen,
            MODULE_LOCAL
        );
    };

    const monthlyTotalDisplay = useMemo(() => {
        if (!calendarData.grid) return 0;
        if (useSymbolProfit) {
            if (typeof calendarData.monthly_total_symbol_profit === "number") {
                return calendarData.monthly_total_symbol_profit;
            }
            return getMonthlyTotalSymbol(calendarData.grid);
        } else {
            if (typeof calendarData.monthly_total === "number") {
                return calendarData.monthly_total;
            }
            return getMonthlyTotal(calendarData.grid);
        }
    }, [calendarData, useSymbolProfit]);

    function computeWeekTotal(week) {
        if (!Array.isArray(week)) return 0;
        return week.reduce((sum, cell) => {
            if (!cell) return sum;
            const val = useSymbolProfit
                ? (typeof cell.symbol_profit === "number" ? cell.symbol_profit : 0)
                : (typeof cell.profit === "number" ? cell.profit : 0);
            return sum + val;
        }, 0);
    }

    if (!dateState.year || !dateState.month) {
        return (
            <div style={{ color: "#fff", padding: 32 }}>
                Loading calendar...
            </div>
        );
    }

    const handleCompareClick = (cell, e) => {
        e.stopPropagation();
        if (!cell || !cell.day) return;
        const clickedDate = getDateString(dateState.year, dateState.month, cell.day);
        setDateClickCount(prev => {
            const newCount = prev + 1;
            setSelectedDates(prevDates => {
                const newDates = newCount % 2 === 1
                    ? { ...prevDates, date_1: clickedDate }
                    : { ...prevDates, date_2: clickedDate };
                sessionStorage.setItem("calendar_date_1", newDates.date_1 || "");
                sessionStorage.setItem("calendar_date_2", newDates.date_2 || "");
                window.dispatchEvent(new Event("calendarDatesChanged"));
                return newDates;
            });
            return newCount;
        });
        handleUIEvent(e, { action: "compare_date", date: clickedDate }, null, screen, MODULE_LOCAL);
    };

    const renderMonthlyTotals = () => (
        <div style={{ marginTop: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h4 style={{ margin: 0 }}>Last 12 Months</h4>
                {calendarData.grand_total !== undefined && (
                    <div style={{ fontSize: "1.1em", fontWeight: "bold" }}>
                        Grand Total(excluding options): <span style={{ color: calendarData.grand_total >= 0 ? "#228B22" : "#B22222" }}>
                            ${calendarData.grand_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                    </div>
                )}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Month</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}>Total P/L</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}># Symbols</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}># Patterns</th>
                    </tr>
                </thead>
                <tbody>
                    {(calendarData.monthly_totals || []).map((row, idx) => (
                        <tr key={idx}>
                            <td style={{ textAlign: "left", padding: "6px 8px" }}>{row.month}</td>
                            <td style={{
                                textAlign: "right",
                                padding: "6px 8px",
                                color: row.total > 0 ? "green" : row.total < 0 ? "red" : "gray"
                            }}>
                                ${row.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td style={{
                                textAlign: "right",
                                padding: "6px 8px",
                                color: "#1976d2",
                                fontWeight: 600
                            }}>
                                {row.unique_symbols ?? "-"}
                            </td>
                            <td style={{
                                textAlign: "right",
                                padding: "6px 8px",
                                color: "#8e24aa",
                                fontWeight: 600
                            }}>
                                {row.unique_patterns ?? "-"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div data-analytics={MODULE_NAME} style={{ display: "flex", gap: 24 }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <h3>
                    🗓️ {mode === "days" ? `${getMonthName(dateState.month)} ${dateState.year}` : "Monthly Totals"}
                    <span
                        className="tooltip-icon"
                        style={{
                            marginLeft: 8,
                            verticalAlign: "middle",
                            cursor: "pointer",
                            color: "#888",
                            fontSize: 18
                        }}
                        title={tooltip}
                    >
                        🛈
                    </span>
                </h3>

                {/* Calendar Selector + Options */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, margin: "12px 0" }}>
                    {/* Calendar Selector - centered above calendar */}
                    {mode === "days" && (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <CalendarSelector
                                    activeDate={navigationDate || sessionStorage.getItem("navigationDate") || sessionStorage.getItem("latest_date")}
                                    onDateChange={handleCalendarSelectorChange}
                                />
                                <span style={{
                                    fontSize: "12px",
                                    color: "#1976d2",
                                    fontWeight: "600",
                                    userSelect: "none",
                                    animation: "bounce 2s infinite",
                                    display: "flex",
                                    alignItems: "center"
                                }}>
                                    ▼
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Mode toggle and options - positioned below calendar selector */}
                    <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "center" }}>
                        {/* Mode toggle */}
                        <span
                            style={{
                                padding: "6px 12px",
                                borderRadius: 4,
                                color: mode === "months" ? "#1976d2" : "#1976d2",
                                fontWeight: mode === "months" ? "bold" : "normal",
                                cursor: "pointer",
                                textDecoration: "underline",
                                fontSize: "0.95em",
                                userSelect: "none",
                                whiteSpace: "nowrap"
                            }}
                            tabIndex={0}
                            role="button"
                            aria-pressed={mode === "months"}
                            onClick={() => setMode(mode === "days" ? "months" : "days")}
                            title={mode === "days" ? "Show last 12 months" : "Show daily calendar"}
                        >
                            {mode === "days" ? "Yearly" : "Daily"}
                        </span>

                        {/* Options toggle */}
                        <span
                            className="toggle-link"
                            onClick={toggleAdditionalOptions}
                            tabIndex={0}
                            role="button"
                            aria-pressed={showAdditionalOptions}
                        >
                            {showAdditionalOptions ? "Hide Options" : "Show Options"}
                            <span className="toggle-arrow">
                                {showAdditionalOptions ? "▲" : "▼"}
                            </span>
                        </span>
                    </div>
                </div>

                {/* Options Panel */}
                {showAdditionalOptions && (
                    <div className="additional-options" ref={additionalOptionsRef} style={{ marginBottom: 12 }}>
                        <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 8, color: "#fff", fontWeight: 500 }}>
                            <input
                                type="checkbox"
                                checked={useSymbolProfit}
                                onChange={handleSymbolProfitToggle}
                                style={{ marginRight: 8 }}
                            />
                            Use symbol profit
                        </label>
                        <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 8, color: "#fff", fontWeight: 500, marginTop: 8 }}>
                            <input
                                type="checkbox"
                                checked={showOpenOrdersLine}
                                onChange={e => setShowOpenOrdersLine(e.target.checked)}
                                style={{ marginRight: 8 }}
                            />
                            Show open orders
                        </label>
                        <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 8, color: "#fff", fontWeight: 500, marginTop: 8 }}>
                            <input
                                type="checkbox"
                                checked={showOptionsLine}
                                onChange={e => setShowOptionsLine(e.target.checked)}
                                style={{ marginRight: 8 }}
                            />
                            Show options
                        </label>
                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 6, lineHeight: 1.3 }}>
                            {useSymbolProfit
                                ? "Showing symbol-specific profit for the largest intra-day quantity symbol each day."
                                : "Showing total daily realized profit (default)."}
                        </div>
                    </div>
                )}

                {mode === "days" && (
                    <>
                        {showAnalysisMode && (
                            <div
                                style={{
                                    position: "fixed",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: "transparent",
                                    zIndex: 99,
                                    pointerEvents: "auto"
                                }}
                                onClick={exitAnalysisMode}
                            />
                        )}

                        <div style={{ position: "relative", zIndex: 100 }}>
                            <table style={{ width: "100%", textAlign: "center", tableLayout: "fixed", marginTop: 8 }}>
                                <thead>
                                    <tr>
                                        {weekdays.map(wd => (
                                            <th key={wd} style={{ padding: "6px 0" }}>{wd}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(calendarData.grid || []).map((week, r) => {
                                        const weekDisplayTotal = computeWeekTotal(week);
                                        const openLineIndices = showOpenOrdersLine ? getOpenOrderLineIndices(week, openOrderDateSet) : null;
                                        const optionsLineIndices = showOptionsLine ? getOpenOrderLineIndices(week, optionsOrderDateSet) : null;
                                        return (
                                            <React.Fragment key={r}>
                                                {openLineIndices && (
                                                    <tr style={{ position: "absolute", left: 0, right: 0, pointerEvents: "none" }}>
                                                        {week.map((_, c) => (
                                                            <td key={c} style={{ padding: 0, border: "none", background: "transparent", height: 0 }}>
                                                                {c === openLineIndices.startIdx && (
                                                                    <div style={{
                                                                        position: "absolute",
                                                                        left: 0,
                                                                        right: 0,
                                                                        top: 0,
                                                                        height: "8px",
                                                                        width: `calc(${(openLineIndices.endIdx - openLineIndices.startIdx + 1) * 100 / 7}% - 10%)`,
                                                                        marginLeft: `${openLineIndices.startIdx * 100 / 7}%`,
                                                                        background: "#ffb703",
                                                                        borderRadius: "4px",
                                                                        zIndex: 10
                                                                    }} title="Open order this week"></div>
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                )}
                                                {optionsLineIndices && (
                                                    <tr style={{ position: "absolute", left: 0, right: 0, pointerEvents: "none" }}>
                                                        {week.map((_, c) => (
                                                            <td key={c} style={{ padding: 0, border: "none", background: "transparent", height: 0 }}>
                                                                {c === optionsLineIndices.startIdx && (
                                                                    <div style={{
                                                                        position: "absolute",
                                                                        left: 0,
                                                                        right: 0,
                                                                        top: "10px",
                                                                        height: "8px",
                                                                        width: `calc(${(optionsLineIndices.endIdx - optionsLineIndices.startIdx + 1) * 100 / 7}% - 10%)`,
                                                                        marginLeft: `${optionsLineIndices.startIdx * 100 / 7}%`,
                                                                        background: "#39FF14",
                                                                        borderRadius: "4px",
                                                                        zIndex: 11
                                                                    }} title="Open options trade this week"></div>
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                )}
                                                <tr>
                                                    {week.map((cell, c) => {
                                                        const cellDateStr = cell && cell.day
                                                            ? getDateString(dateState.year, dateState.month, cell.day)
                                                            : null;
                                                        const isActive = cellDateStr && cellDateStr === analysisActiveDate;
                                                        const displayProfit = cell
                                                            ? (useSymbolProfit
                                                                ? cell.symbol_profit
                                                                : cell.profit)
                                                            : undefined;
                                                        const cellHasDate = cell && cell.day;
                                                        const hasTrade = cellHasDate && typeof displayProfit === "number" && displayProfit !== 0;

                                                        return (
                                                            <td
                                                                key={c}
                                                                style={{
                                                                    verticalAlign: "top",
                                                                    padding: "0",
                                                                    background: c === 6 ? "#223a5f" : undefined,
                                                                    cursor: cellHasDate ? "pointer" : "default",
                                                                    position: "relative",
                                                                    transition: "all 0.3s ease"
                                                                }}
                                                                onClick={() => cellHasDate && handleDayTileClick(cell, event)}
                                                            >
                                                                {cell && (
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                            gap: 4,
                                                                            minHeight: isActive ? 140 : showAnalysisMode ? 80 : 50,
                                                                            padding: isActive ? "12px 8px" : "8px 4px",
                                                                            justifyContent: isActive ? "flex-start" : "space-between",
                                                                            transition: "all 0.3s ease",
                                                                            border: isActive
                                                                                ? "2px solid #1976d2"
                                                                                : showAnalysisMode
                                                                                    ? "1px solid #ccc"
                                                                                    : "none",
                                                                            borderRadius: isActive ? 8 : 4,
                                                                            boxShadow: isActive ? "0 4px 12px rgba(25, 118, 210, 0.3)" : "none"
                                                                        }}
                                                                    >
                                                                        <div style={{ textAlign: "center" }}>
                                                                            {cell.notesAvailable && (
                                                                                <span
                                                                                    title="Daily note available"
                                                                                    style={{
                                                                                        position: "absolute",
                                                                                        top: 2,
                                                                                        right: 6,
                                                                                        fontSize: "0.55em",
                                                                                        color: "#1976d2",
                                                                                        pointerEvents: "none",
                                                                                        zIndex: 2
                                                                                    }}
                                                                                >
                                                                                    📝
                                                                                </span>
                                                                            )}
                                                                            {cell.day && (
                                                                                <>
                                                                                    <span style={{ color: "gray", fontWeight: "bold", fontSize: "0.95em" }}>
                                                                                        {cell.day}
                                                                                    </span>
                                                                                    {displayProfit !== undefined && (
                                                                                        <>
                                                                                            <br />
                                                                                            <span style={{
                                                                                                color: displayProfit > 0 ? "green" : displayProfit < 0 ? "red" : "gray",
                                                                                                fontSize: "0.85em",
                                                                                                fontWeight: "600"
                                                                                            }}>
                                                                                                ${displayProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                            </span>
                                                                                        </>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>

                                                                        {isActive && hasTrade && (
                                                                            <div
                                                                                style={{
                                                                                    display: "flex",
                                                                                    flexDirection: "column",
                                                                                    gap: 8,
                                                                                    marginTop: 8,
                                                                                    opacity: isActive ? 1 : 0,
                                                                                    transition: "opacity 0.3s ease 0.1s"
                                                                                }}
                                                                            >
                                                                                <button
                                                                                    style={{
                                                                                        fontSize: "0.8em",
                                                                                        padding: "6px 10px",
                                                                                        borderRadius: 4,
                                                                                        border: "1px solid #1976d2",
                                                                                        background: "#f5faff",
                                                                                        color: "#1976d2",
                                                                                        cursor: "pointer",
                                                                                        fontWeight: "600",
                                                                                        transition: "all 0.2s ease"
                                                                                    }}
                                                                                    onClick={e => {
                                                                                        e.stopPropagation();
                                                                                        handleCompareClick(cell, e);
                                                                                    }}
                                                                                    title="Compare this date"
                                                                                    onMouseEnter={e => {
                                                                                        e.target.style.background = "#e3f2fd";
                                                                                        e.target.style.boxShadow = "0 2px 6px rgba(25, 118, 210, 0.2)";
                                                                                    }}
                                                                                    onMouseLeave={e => {
                                                                                        e.target.style.background = "#f5faff";
                                                                                        e.target.style.boxShadow = "none";
                                                                                    }}
                                                                                >
                                                                                    Compare
                                                                                </button>
                                                                                <span
                                                                                    style={{
                                                                                        fontSize: "0.8em",
                                                                                        color: "#1976d2",
                                                                                        textDecoration: "underline",
                                                                                        cursor: "pointer",
                                                                                        fontWeight: "600",
                                                                                        textAlign: "center",
                                                                                        transition: "color 0.2s ease"
                                                                                    }}
                                                                                    title="View details for this date"
                                                                                    onClick={e => {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        const clickedDate = getDateString(dateState.year, dateState.month, cell.day);
                                                                                        sessionStorage.setItem("navigationDate", clickedDate);
                                                                                        if (props.onNavigate) {
                                                                                            props.onNavigate("pg_details", clickedDate);
                                                                                        }
                                                                                    }}
                                                                                    onMouseEnter={e => {
                                                                                        e.target.style.color = "#0d47a1";
                                                                                    }}
                                                                                    onMouseLeave={e => {
                                                                                        e.target.style.color = "#1976d2";
                                                                                    }}
                                                                                >
                                                                                    Details
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        {c === 6 && (cell?.weekTotal !== undefined) &&
                                                                            <div style={{
                                                                                fontSize: "0.8em",
                                                                                color: cell.weekTotal > 0 ? "green" : cell.weekTotal < 0 ? "red" : "gray",
                                                                                fontWeight: "bold",
                                                                                marginTop: isActive ? 8 : 2,
                                                                                textAlign: "center",
                                                                                opacity: 1,
                                                                                transition: "opacity 0.3s ease"
                                                                            }}>
                                                                                Week: ${cell.weekTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                    <tr>
                                        {Array.from({ length: 7 }).map((_, c) => (
                                            <td key={c} style={{ padding: "8px 0", borderTop: "2px solid #ccc" }}>
                                                {c === 0 && calendarData.grand_total !== undefined && (
                                                    <div style={{ textAlign: "left", paddingLeft: 8 }}>
                                                        <span style={{ fontSize: "0.9em", color: "#888" }}> </span>
                                                        <span style={{ 
                                                            fontSize: "1em", 
                                                            fontWeight: "bold",
                                                            color: calendarData.grand_total >= 0 ? "green" : "red"
                                                        }}>
                                                        </span>
                                                    </div>
                                                )}
                                                {c === 6 && calendarData.grid && (
                                                    <div style={{ textAlign: "right", paddingRight: 8 }}>
                                                        <span style={{ fontSize: "0.9em", color: "#888" }}>Monthly Total: </span>
                                                        <span style={{
                                                            fontSize: "1em",
                                                            color: (monthlyTotalDisplay > 0 ? "green" : monthlyTotalDisplay < 0 ? "red" : "gray"),
                                                            fontWeight: "bold"
                                                        }}>
                                                            ${monthlyTotalDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {showAnalysisMode && (
                            <div style={{ marginTop: 16, fontSize: "0.95em", display: "flex", gap: 32 }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ fontWeight: 500 }}>Date 1:</span>
                                    <span style={{
                                        marginLeft: 8,
                                        color: selectedDates.date_1 ? "#1976d2" : "#888"
                                    }}>
                                        {selectedDates.date_1
                                            ? formatMMDDYY(selectedDates.date_1)
                                            : <span style={{ color: "#bbb" }}>Not selected</span>}
                                    </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ fontWeight: 500 }}>Date 2:</span>
                                    <span style={{
                                        marginLeft: 8,
                                        color: selectedDates.date_2 ? "#1976d2" : "#888"
                                    }}>
                                        {selectedDates.date_2
                                            ? formatMMDDYY(selectedDates.date_2)
                                            : <span style={{ color: "#bbb" }}>Not selected</span>}
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {mode === "months" && renderMonthlyTotals()}
            </div>
        </div>
    );
}
